import {Tooltip} from 'platform/components';
import {HStack, Icon, Text, ThemeIconKey} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {forwardRef} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useTooltipProps} from '../../hooks/useTooltipProps';
import {ICellRenderer, ICellRendererParams} from '../../types/AgGridTypes';
import {TooltipGetter} from '../../types/TooltipGetter';
import {getCellClassName} from '../../utils/getCellClassName';
import {getStylesToRender} from '../../utils/getStylesToRender';
import {getValueToRender} from '../../utils/getValueToRender';
import {getMappedIconColor} from './utils/getMappedIconColor';

export interface IconCellRendererProps extends ICellRendererParams {
  getTooltipProps?: TooltipGetter;
}

const isThemeIconKey = (
  iconType: string | undefined,
  icons: Record<string, unknown>
): iconType is ThemeIconKey =>
  isNotNil(iconType) && Object.prototype.hasOwnProperty.call(icons, iconType);

function IconCellRendererComponent(props: IconCellRendererProps, _ref: React.Ref<ICellRenderer>) {
  const theme = useTheme();
  const className = getCellClassName(props.colDef);
  const cellValue = getValueToRender(props);
  const tooltipProps = useTooltipProps(props.data, props.node, props.getTooltipProps);
  const cellStyles = getStylesToRender(props);
  const iconType = cellStyles?.icon?.type;
  const mappedIconColor = getMappedIconColor(cellStyles?.icon?.color);
  const isValidIcon = isThemeIconKey(iconType, theme.icons) && !isNil(mappedIconColor);

  if (!isValidIcon) {
    return (
      <Tooltip {...tooltipProps}>
        {/* eslint-disable-next-line react/forbid-dom-props */}
        <div className={className}>{cellValue}</div>
      </Tooltip>
    );
  }

  return (
    <Tooltip {...tooltipProps}>
      {/* eslint-disable-next-line react/forbid-dom-props */}
      <div className={className}>
        <HStack spacing={1}>
          <Icon value={iconType} color={mappedIconColor} size={4} />
          <Text color={cellStyles?.textColor} noWrap={!props.colDef?.wrapText} size="small">
            {cellValue}
          </Text>
        </HStack>
      </div>
    </Tooltip>
  );
}

export const IconCellRenderer = forwardRef<ICellRenderer, IconCellRendererProps>(
  IconCellRendererComponent
);
